import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import FeatherIcon from 'feather-icons-react';
import { InspectionTrackerListTable } from './Components/InspectionTrackerListTable';
import { PropertySelector } from './Components/PropertySelector';
import { DeleteInspectionConfirmationModal } from './Components/DeleteInspectionConfirmationModal';
import { InspectionTypesTable } from './Components/InspectionTypesTable';
import { InspectionFrequenciesTable } from './Components/InspectionFrequenciesTable';
import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';
import {
  createInspection,
  getInspections,
  updateInspection,
  deleteInspection,
  exportInspectionsToExcel,
  getInspectionTypes,
  getInspectionFrequencies,
} from './Components/FormikHelpers/Api';
import { setLoading } from '../../../../store/actions';

const TABS = {
  TRACKER: 'tracker',
  TYPES: 'types',
  FREQUENCIES: 'frequencies',
};

const InspectionTracker = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.TRACKER);
  const [properties, setProperties] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [filters, setFilters] = useState({
    property_id: '',
    building_name: '',
    property_type: '',
    year: '',
    overdueOnly: false,
    frequencyId: '',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchSelectOptions = useCallback(async () => {
    try {
      const [typesRes, freqsRes] = await Promise.all([
        getInspectionTypes({ activeOnly: 'true' }),
        getInspectionFrequencies({ activeOnly: 'true' }),
      ]);
      setTypeOptions(
        (typesRes?.data || []).map((t) => ({
          value: t._id,
          label: t.name,
          defaultContractor: t.defaultContractor,
        }))
      );
      setFrequencyOptions(
        (freqsRes?.data || []).map((f) => ({
          value: f._id,
          label: f.name,
          days: f.days,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch select options:', err);
    }
  }, []);

  const fetchInspections = useCallback(async () => {
    if (!filters.property_id) {
      setInspections([]);
      return;
    }
    try {
      dispatch(setLoading(true));
      const params = {
        propertyId: filters.property_id,
        buildingName: filters.building_name || undefined,
        year: filters.year || undefined,
        overdueOnly: filters.overdueOnly || undefined,
        frequencyId: filters.frequencyId || undefined,
      };
      const response = await getInspections(params);
      setInspections(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch inspections:', error);
      setInspections([]);
      Swal.fire('Error', 'Failed to load inspection records', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, filters]);

  const handleSaveRow = useCallback(
    async (rowData) => {
      try {
        dispatch(setLoading(true));
        const { _isNew, _id, ...payload } = rowData;
        let response;
        if (_id) {
          response = await updateInspection(_id, payload);
        } else {
          response = await createInspection(payload);
        }
        await fetchInspections();
        return { success: true, data: response?.data || response };
      } catch (error) {
        console.error('Failed to save inspection:', error);
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, fetchInspections]
  );

  const handleDeleteConfirm = async () => {
    if (!selectedInspection?._id) return;
    try {
      dispatch(setLoading(true));
      await deleteInspection(selectedInspection._id);
      Swal.fire('Deleted', 'Inspection record deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedInspection(null);
      fetchInspections();
    } catch {
      Swal.fire('Error', 'Failed to delete inspection', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleExportExcel = () => {
    if (!filters.property_id) {
      toast.warn('Please select a property first');
      return;
    }
    exportInspectionsToExcel({
      propertyId: filters.property_id,
      buildingName: filters.building_name || undefined,
      year: filters.year || undefined,
      overdueOnly: filters.overdueOnly || undefined,
    });
  };

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchInspections(); }, [fetchInspections]);
  useEffect(() => { fetchSelectOptions(); }, [fetchSelectOptions]);
  const refreshSelectOptions = useCallback(() => {
    fetchSelectOptions();
  }, [fetchSelectOptions]);

  const tabStyle = (tab) => ({
    cursor: 'pointer',
    padding: '8px 20px',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: activeTab === tab ? '#1A6B4A' : '#6B8F7C',
    borderBottom: activeTab === tab ? '2px solid #1A6B4A' : '2px solid transparent',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid #1A6B4A' : '2px solid transparent',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  });

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Inspection Tracker</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Inspection Tracker" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24, borderBottom: '1px solid #D4E8DC' }}>
          <button style={tabStyle(TABS.TRACKER)} onClick={() => setActiveTab(TABS.TRACKER)}>
            <FeatherIcon icon="clipboard" size={14} />
            Tracker
          </button>
          <button style={tabStyle(TABS.TYPES)} onClick={() => setActiveTab(TABS.TYPES)}>
            <FeatherIcon icon="tag" size={14} />
            Inspection Types
          </button>
          <button style={tabStyle(TABS.FREQUENCIES)} onClick={() => setActiveTab(TABS.FREQUENCIES)}>
            <FeatherIcon icon="clock" size={14} />
            Frequencies
          </button>
        </div>

        {activeTab === TABS.TRACKER && (
          <>
            <PropertySelector
              propertyList={properties}
              selectedFilters={filters}
              onPropertySelectionChange={setFilters}
              onExportExcel={handleExportExcel}
              frequencyOptions={frequencyOptions}
            />
            <InspectionTrackerListTable
              items={inspections}
              properties={properties}
              filters={filters}
              onDelete={(inspection) => { setSelectedInspection(inspection); setIsDeleteModalOpen(true); }}
              onSaveRow={handleSaveRow}
              onRefresh={fetchInspections}
              userRole={user?.role || 'other'}
              typeOptions={typeOptions}
              frequencyOptions={frequencyOptions}
            />
            <DeleteInspectionConfirmationModal
              open={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              onDelete={handleDeleteConfirm}
              inspection={selectedInspection}
            />
          </>
        )}
        {activeTab === TABS.TYPES && (<InspectionTypesTable onRefresh={refreshSelectOptions} />)}
        {activeTab === TABS.FREQUENCIES && (<InspectionFrequenciesTable onRefresh={refreshSelectOptions} />)}
      </div>
    </div>
  );
};

export { InspectionTracker };